import React, { useEffect, useMemo } from "react";
import { Stack, FormControlLabel, Checkbox, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createTerm, updateTerm } from "../redux/actions/termActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { termActions } from "../redux/slices/term";
import { cleanFormData } from "../helpers/formHelpers";
import { formatDateForInput } from "../helpers/dateHelpers";

import { TermResponseDto } from "../shared/dtos/term/TermResponseDto";
import { TermRequestDto } from "../shared/dtos/term/TermRequestDto";

const termSchema = Yup.object().shape({
  name: Yup.string().required("Term name is required (e.g., Term 1)"),
  academicYearId: Yup.string().required("Academic Year is required"),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date must be after start date"),
  isCurrentTerm: Yup.boolean().required(),
});

interface CreateOrUpdateTermFormProps {
  term?: TermResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateTermForm: React.FC<CreateOrUpdateTermFormProps> = ({
  term = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Fetch Academic Years for the Dropdown
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );

  useEffect(() => {
    if (!academicYears || academicYears.length === 0) {
      dispatch(getAcademicYears());
    }
  }, [dispatch, academicYears]);

  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const initialValues: TermRequestDto = {
    name: term?.name || "",
    academicYearId: term?.academicYearId || "",
    startDate: formatDateForInput(term?.startDate),
    endDate: formatDateForInput(term?.endDate),
    isCurrentTerm: term?.isCurrentTerm || false,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(termActions.setErrorUpdate(null))
            : () => dispatch(termActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={termSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: TermRequestDto = {
            name: rawCleaned.name,
            academicYearId: rawCleaned.academicYearId,
            startDate: rawCleaned.startDate,
            endDate: rawCleaned.endDate,
            isCurrentTerm: Boolean(values.isCurrentTerm),
          };

          if (isEditing && term) {
            dispatch(updateTerm({ id: term.id, values: cleanedValues }));
          } else {
            dispatch(createTerm(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({
          dirty,
          isValid,
          values,
          handleChange,
        }: FormikProps<TermRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="academicYearId"
                  label="Academic Year"
                  options={yearOptions}
                />
                <FormTextField name="name" label="Term Name (e.g., Term 1)" />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="startDate"
                  label="Start Date"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormTextField
                  name="endDate"
                  label="End Date"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isCurrentTerm}
                    onChange={handleChange}
                    name="isCurrentTerm"
                    color="primary"
                  />
                }
                label="Set as Current Term"
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Term"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateTermForm;
